import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill.js';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill.js';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
// icons
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// styles
import { TypographyStyled } from './styles';

export default () => (
  <SectionContainer
    id='annuity-faq-common-questions-answers'
    backgroundVariant='lightA'>
    <Row align='center'>
      <Col medium={8}>
        <Typography component='h2' nextElement='typography'>
          Answers to Common <WidowBlock>1099 Questions</WidowBlock>
        </Typography>
        <Typography component='h3' nextElement='typography'>
          General
        </Typography>
        <Accordion
          id='faq-annuity-faq-one'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          titleTypographyProps={{ component: 'h3' }}
          isFAQ
          addFAQPageSchemaItemType={false}
          items={[
            {
              id: 'annuity-faq-access-common-questions-answers-when-is-receive-faq',
              title: <>When can I expect to receive my Form 1099&#45;R <WidowBlock>or 1099&#45;INT&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The Internal Revenue Service &#40;IRS&#41; and the Internal Revenue Code &#40;IRC&#41; require Northwestern Mutual
                    mail Form 1099&#45;R and 1099&#45;INT to the taxpayer by January 31st &#40;or the next business day if January 31st
                    falls on a weekend or legal holiday&#41; of the year following the year in which the distribution took place or
                    the interest was earned. Example: A reportable transaction occurs by December 31st then a tax form 1099
                    needs to be sent by January 31st of the following year. Given the large volume of tax forms mailed during January, it may take
                    several days for the form to reach the taxpayer. Form 1099s are also available through the Northwestern
                    Mutual website as early as February 1<sup>st</sup>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-how-can-access-faq',
              title: <>How can I access my <WidowBlock>1099 online&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    <Link
                      id='annuity-faq-client-website-login-link'
                      linkUrl='https://plan.northwesternmutual.com/'
                      variant='primaryDeemphasize'>
                      Log into our client website
                    </Link>
                    &nbsp;and select the Documents page from the drop down menu. 1099 documents will be displayed under the document type &#8220;Tax Documents&#8221;.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-why-receive-1099-faq',
              title: <>Why am I receiving a Form 1099 from <WidowBlock>Northwestern Mutual&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The Internal Revenue Service &#40;IRS&#41; and the Internal Revenue Code &#40;IRC&#41; require Northwestern Mutual to
                    report certain distributions received from life insurance and annuity contracts, and interest earned. The
                    IRS requires that we report this information on a Form 1099 to both the taxpayer and the IRS. Some common
                    examples of taxable income Northwestern Mutual is required to report include:
                  </Typography>
                  <Typography nextElement='typography'><strong>Form 1099&#45;INT</strong> &#8211; Interest earned from a Billing&#47;Payment Account,
                    interest earned on dividends that are left to accumulate &#40;i.e. Accumulated Dividends&#41;, interest income
                    plans, interest earned on death proceeds after the date of death, but prior to the proceeds being paid, and
                    interest earned on Access Fund Accounts; if the amount of the interest is at least &#36;10.00.
                  </Typography>
                  <Typography nextElement='typography'>
                    <strong>Form 1099&#45;R, Distributions From Pensions, Annuities, &#8230; Insurance Contracts,
                      etc.</strong>&#8212;Full and partial distributions and certain dividend payments from life insurance and annuity
                    contracts. Note: Certain tax&#45;free exchanges are also reportable on Form 1099&#45;R for informational
                    purposes.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-can-nm-advise-on-tax-faq',
              title: <>Can Northwestern Mutual advise me on <WidowBlock>tax matters&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    No, Northwestern Mutual cannot provide tax advice as tax laws are complex and circumstances can vary greatly. This FAQ provides answers to common questions about the Forms 1099 and what is reported on them. For tax advice you should consult with your tax advisor. For example, if you received a 1099&#45;R indicating &#8220;taxable amount not determined&#8221; you may want to discuss with a tax advisor to determine the
                    taxable portion. You may find helpful information on the <Link id='annuity-faq-internal-revenue-website-link' linkUrl="https://www.irs.gov/"
                      isOutgoingLink variant='primaryDeemphasize'>Internal Revenue Service site</Link> including IRS Publication 17, which provides information
                    on a variety of topics and is searchable.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-contract-has-multiple-owners-only-one-faq',
              title: <>This contract has multiple owners, why was only one <WidowBlock>1099 issued&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>Northwestern Mutual records that a contract has multiple owners, but we do not have knowledge of the
                    arrangement among the owners of the contract. The IRS specifies that for multiple owners, the reporting company may report to the first owner of record to fulfill its reporting obligation. The first owner of record, in turn, can divide and report the income to the other owners &#40;referred to as &#8220;nominees&#8221;&#41; on separate Forms 1099 based on their respective ownership interests. If you need to nominate additional owners you may want to consult with your tax advisor or refer
                    to <Link id='annuity-faq-information-returns-instructions-link' linkUrl="https://www.irs.gov/pub/irs-pdf/i1099gi.pdf" isOutgoingLink variant='primaryDeemphasize'>General Instructions for Certain
                      Information Returns &#40;Forms 1097, 1098, 1099, 3921, 3922, 5498, and W&#45;2G&#41;.</Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-can-download-nm-form-faq',
              title: <>Can I download my Northwestern Mutual Form 1099 information directly into my tax <WidowBlock>preparation software&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Currently, Northwestern Mutual does not provide this service for life insurance or annuity products.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-tax-form-in-mail-and-online-faq',
              title: <>Will I receive my tax form in the mail and also see <WidowBlock>it online&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Yes, Northwestern Mutual will mail tax forms, but also most will be viewable online to those that opted in for Online Access.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-why-multiple-images-faq',
              title: <>Why do I see multiple images of some of <WidowBlock>my 1099s&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    If you have multiple life and&#47;or annuity accounts that generate a 1099, NM has consolidated into one document. However, when you view online, you may see more than one image of the same document. We apologize for any confusion.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-file-taxes-differently-if-multiple-faq',
              title: <>If I see multiple 1099 forms, do I file taxes <WidowBlock>any differently&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    No. The information is the same, just in duplicate.
                  </Typography>
                  <ul>
                    <li>
                      <Typography nextElement='typography'>
                        Note: If certain matched criteria is met the tax form will be mailed in consolidation form. A consolidated tax form will have detail for each policy taxable event detailed on page 3 of tax form. If you have access to online viewing and view a consolidated form online it may show that identical form multiple times. Please know the duplicates can be ignored and be assured they are not filed multiple times with the IRS.
                      </Typography>
                    </li>
                  </ul>
                </>
              ),
            },
          ]}
        />
        <TypographyStyled component='h3' nextElement='typography'>
          <WidowBlock>Form 1099&#45;INT:</WidowBlock>
        </TypographyStyled>
        <Accordion
          id='faq-annuity-faq-two'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          titleTypographyProps={{ component: 'h3' }}
          isFAQ
          addFAQPageSchemaItemType={false}
          items={[
            {
              id: 'annuity-faq-access-common-questions-answers-why-receive-death-benefit-tax-free-faq',
              title: <>I received a Form 1099&#45;INT as beneficiary of a life insurance contract. I thought death benefits were tax&#45;free. Why did I receive a <WidowBlock>Form 1099&#45;INT&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    While the death benefits from a life insurance contract are generally tax&#45;free, interest may be paid from the date of death until the claim is settled. The IRS requires that we report the interest you earned on Form 1099&#45;INT.
                  </Typography>
                </>
              ),
            },
          ]}
        />
        <TypographyStyled component='h3' nextElement='typography'>
          <WidowBlock>Form 1099&#45;R:</WidowBlock>
        </TypographyStyled>
        <Accordion
          id='faq-annuity-faq-three'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          titleTypographyProps={{ component: 'h3' }}
          isFAQ
          addFAQPageSchemaItemType={false}
          items={[
            {
              id: 'annuity-faq-access-common-questions-answers-how-taxable-gain-reported-faq',
              title: <>How should the taxable gain on Form 1099&#45;R be reported &#8211;&#8211; ordinary income or <WidowBlock>capital gains&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Generally, the gain reported on the full or partial distribution of a life insurance or annuity contract is considered ordinary income. There are some situations in which the proceeds are treated as capital gain, e.g. the life insurance contract was sold by an owner to another party. If you feel your transaction may qualify for capital gain treatment you should consult with your tax advisor.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-how-is-taxable-gain-reported-for-life-insurance-faq',
              title: <>How is the taxable amount reported on the Form 1099&#45;R determined for a life <WidowBlock>insurance contract&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    For any part of a transaction to be taxable, the contract must first have a gain. A life insurance contract has a gain when the cash value exceeds the cost basis &#40;generally premiums paid less adjustments&#41; of the contract.
                  </Typography>
                  <Typography nextElement='typography'>
                    Generally, when a taxpayer takes a distribution from a life insurance contract the IRC allows the taxpayer to recover the cost basis before any gain is withdrawn, but there are some exceptions. Distributions &#40;including loans&#41; from life insurance contracts classified as Modified Endowment Contracts &#40;MECs&#41; under the IRC are taxed gain&#45;out&#45;first and may be subject to an additional 10&#37; penalty tax. Also, distributions from a contract within the first 15 contract years may be subject to gain&#45;out&#45;first taxation even if the policy is not a MEC, depending on the size of the withdrawal and other
                    factors.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-loan-surrendered-lapsed-gross-greater-than-received-faq',
              title: <>There was a loan on my life insurance contract when it was surrendered&#47;lapsed. Why is the gross distribution on the Form 1099&#45;R greater than the amount I received at <WidowBlock>that time&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    If a policy lapses or is surrendered prior to the insured&#8217;s death, the loan will be repaid from the policy&#8217;s cash value. This is treated as though the owner received the surrender proceeds and then immediately used them to repay the loans. Under the tax law, this transaction is a distribution from a life insurance policy, which we are required to report on an IRS Form 1099R if there is gain in the policy. The remaining cash value, if any, is distributed to
                    the contract owner.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-exchanged-life-insurance-for-another-faq',
              title: <>I exchanged my life insurance contract for another life insurance contract. Why did I receive a Form 1099&#45;R &#40;or possibly two <WidowBlock>Forms 1099&#45;R&#41;&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The IRC allows certain exchanges &#40;section 1035 tax&#45;free exchanges&#41;, to occur without generating taxable income even though one contract is surrendered for another.
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>If you exchanged your Northwestern Mutual contract for one with a different company, and the exchange qualified as an IRC section 1035 tax&#45;free exchange, a Form 1099&#45;R was generated showing a Distribution Code of &#8220;6&#8221;in Box 7. This provides the cost basis transferred &#40;amount in Box 5&#41; to your new contract as part of the exchange. No taxable amount is reported in Box 2.</> },
                      { bulletText: <>If you received two Forms 1099&#45;R for the same contract on the exchange from Northwestern Mutual to another company, the exchange was not entirely tax&#45;free. One Form 1099&#45;R will report the amount of taxable income generated from the exchange &#40;Distribution Code of &#8220;7&#8221; in Box 7&#41;, which occurs if you received cash or an outstanding loan was extinguished as part of the exchange. The other Form 1099&#45;R reports the cost basis transferred &#40;Distribution Code of &#8220;6&#8221; in Box 7&#41; to the new company. If you exchanged one Northwestern Mutual contract for another Northwestern Mutual contract under IRC section 1035, we do not send a 1099&#45;R with a Distribution Code of &#8220;6&#8221;as Northwestern Mutual tracks the cost basis internally.</> },
                      { bulletText: <>If your contract did not qualify for the exchange, the surrender was taxable to the extent of the gain and reported on Form 1099&#45;R with a Distribution Code of &#8220;7&#8221; in Box 7.</> },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-surrendered-multiple-only-received-one-faq',
              title: <>I surrendered multiple life insurance or annuity contracts. Why did I only receive one <WidowBlock>Form 1099&#45;R&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The IRS allows for consolidating information from multiple contracts on one form, if the information is similar. As a mutual insurance company that is owned by the policyowners, we take advantage of this opportunity to reduce costs. We do include a breakdown by contract on the page following the Form 1099&#45;R.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-received-form-reporting-as-taxable-faq',
              title: <>I received a Form 1099&#45;R reporting all or part of my annual dividend on my life insurance or annuity contract as <WidowBlock>taxable. Why&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Generally, for life insurance, the IRC allows the taxpayer to recover the cost basis of the contract before anything is taxable. If the cost basis was recovered through previous withdrawals, then subsequent dividends that are paid in cash, applied to a contract loan, or deposited into a Billing/Payment Account are
                    reported as taxable income. Dividends from an annuity are fully taxable.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-dividend-reported-as-taxable-faq',
              title: <>My contract dividend was reported as taxable; shouldn&#39;t that dividend have been reported on <WidowBlock>Form 1099&#45;DIV&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    No, per the IRS instructions, taxable dividend distributions from life insurance and annuity contracts are reported on Form 1099&#45;R &#8211; Distributions From Pensions, Annuities, &#8230; Insurance Contracts, etc. The Form 1099&#45;DIV is generally used for dividends paid on corporate stock.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-can-this-be-corrected-faq',
              title: <>I received a Form 1099&#45;R with a Distribution Code of &#8220;1&#8221; in Box 7 indicating that I am subject to a penalty tax because at the time of the distribution I was younger than age 59 &#189;. While I was younger than 59 &#189;, I was also disabled and should qualify for an exception under IRC section 72. Can this Form 1099&#45;R <WidowBlock>be corrected&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    While you may qualify for the disability exception, the definition of disability can vary and to qualify you must meet the IRS definition of disability. Northwestern Mutual is not in the position to make the determination of whether you qualify under the IRS definition. You should consult with a tax advisor regarding claiming the exception on your federal tax return.
                  </Typography>
                </>
              ),
            },
            {
              id: 'annuity-faq-access-common-questions-answers-what-to-do-if-info-is-incorrect-faq',
              title: <>What should I do if I think some of the information on my 1099&#45;INT&#47;1099&#45;R <WidowBlock>is incorrect&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Information believed to be incorrect:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: 'Social Security Number or Taxpayer Identification Number' },
                      { bulletText: 'Address' },
                      { bulletText: 'Your tax form was submitted to the state of your secondary residence, rather than your resident state.' },
                    ]}
                  />
                  <Typography nextElement='typography'>
                    Call the telephone number on the upper right&#45;hand corner of the Instructions for Recipient page that was sent along with your 1099.
                  </Typography>
                  <Typography nextElement='typography'>
                    Information believed to be incorrect:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: 'Dollar amounts' },
                      { bulletText: 'Distribution Code' },
                    ]}
                  />
                  <Typography nextElement='typography'>
                    If the information relates to an amount or distribution code, you can call the number on the Instructions for Recipient page as well. However, prior to doing so, please consider:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>In some instances the information on the 1099 may be combined from multiple policies&#47;contracts or transactions and may account for an apparent discrepancy&#8212;consider all of the transactions that were completed during the tax year.</> },
                      { bulletText: 'The reporting and determination of taxable income from life insurance policy and annuity contract is governed by Internal Revenue Code, and not a contractual stipulation on the part of Northwestern Mutual. Northwestern Mutual is required by law to report certain taxable transactions from life insurance policies and annuity contracts.' },
                    ]}
                  />
                  <Typography nextElement='typography'>
                    There may be instances where Northwestern Mutual is required to report a transaction as taxable in a certain manner, but for reasons not reflected in Northwestern Mutual&#8221;s records, the distribution may
                    qualify for different tax treatment. In these situations you may need to provide additional information to the Internal Revenue Service &#40;not Northwestern Mutual&#41; to support your position regarding the amount of
                    income that is taxable. Northwestern Mutual cannot determine the impact of transactions that are not fully reflected in our records. You should seek tax advice from a tax advisor.
                  </Typography>
                  <Typography nextElement='typography'>
                    <strong>This FAQ is not intended to be tax advice and cannot be used for the purpose of avoiding penalties that may be imposed under the Internal Revenue Code or any other applicable tax law and you are advised to seek tax advice based upon your own particular circumstances from an independent tax
                      advisor.</strong>
                  </Typography>
                  <Typography disableBottomPadding>
                    <strong>Effective tax year 2020, the IRS has made changes to tax form 1099MISC by removing Non&#45;Employee Compensation (NEC) to
                      its own 1099NEC form. To see further detail regarding IRS tax forms, please visit <Link id='annuity-faq-irs-website-link' linkUrl="https://www.irs.gov/"
                      isOutgoingLink variant='primaryDeemphasize'>IRS.GOV</Link> or consult with your professional tax advisor.</strong>
                  </Typography>
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
